import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Download } from "lucide-react";
import { API, statusLabel, fmtDate, STATUS_COLOR } from "./constants";
import { DataTable, Pager } from "./DataTable";

export function EmployersTab({ presetStatus, onOpen }) {
  const [data, setData] = useState({ items: [], total: 0, statuses: [] });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(presetStatus || "");
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const params = new URLSearchParams({ page: String(page), limit: "25" });
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    const res = await api.get(`/admin-ops/employers?${params}`);
    setData(res.data);
  }, [page, q, status]);

  useEffect(() => { setStatus(presetStatus || ""); setPage(1); }, [presetStatus]);
  useEffect(() => {
    const t = setTimeout(() => load().catch((e) => setError(formatApiError(e))), 250);
    return () => clearTimeout(t);
  }, [load]);

  const pages = Math.max(1, Math.ceil(data.total / 25));
  return (
    <div className="space-y-3">
      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
      <div className="flex flex-wrap gap-2">
        <input data-testid="employers-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
          placeholder="Rechercher (entreprise, contact, courriel)…"
          className="flex-1 min-w-[220px] text-sm border border-slate-200 rounded-lg px-3.5 py-2.5" />
        <select data-testid="employers-status-filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white">
          <option value="">Tous les statuts commerciaux</option>
          {data.statuses.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
        <a data-testid="employers-export" href={`${API}/api/admin-ops/export?type=employers`}
          className="flex items-center gap-1.5 text-xs font-semibold text-brand-navy border border-slate-200 rounded-lg px-3.5 py-2.5 hover:border-brand-turquoise">
          <Download className="w-3.5 h-3.5" /> Exporter CSV
        </a>
      </div>
      <DataTable testid="employers-table" rowKey="employer_id" onRowClick={onOpen}
        empty="Aucun employeur ne correspond aux filtres."
        columns={[
          { key: "company_name", label: "Entreprise", render: (r) => <span className="font-semibold text-brand-navy">{r.company_name}</span> },
          { key: "contact_name", label: "Contact" },
          { key: "email", label: "Courriel" },
          { key: "city", label: "Ville" },
          { key: "sector", label: "Secteur" },
          { key: "offers_count", label: "Offres" },
          { key: "commercial_status", label: "Statut commercial", render: (r) => (
            <span className={`text-xs font-medium rounded-full px-2.5 py-1 ${STATUS_COLOR(r.commercial_status)}`}>{statusLabel(r.commercial_status)}</span>
          ) },
          { key: "next_action_date", label: "Relance", render: (r) => (
            <span className={r.next_action_date && r.next_action_date < new Date().toISOString().slice(0, 10) ? "text-red-600 font-semibold" : ""}>
              {r.next_action_date || "—"}
            </span>
          ) },
          { key: "last_interaction", label: "Dernière interaction", render: (r) => fmtDate(r.last_interaction) },
        ]}
        rows={data.items} />
      <Pager testid="employers-pager" page={page} pages={pages} total={data.total} onPage={(p) => setPage(Math.min(Math.max(1, p), pages))} />
    </div>
  );
}
